import React from "react";
import PoweredByDLSoft from "@/components/branding/PoweredByDLSoft";

export interface LoadingScreenProps {
  /** Mensaje opcional debajo del logo */
  message?: string;
  /** Variante visual */
  variant?: "fullscreen" | "inline" | "overlay";
  /** Ocultar el texto de marca */
  hideBrand?: boolean;
}

/**
 * Pantalla de carga premium de Insignia.
 * - Logo animado con efecto de pulso y glow
 * - Anillos orbitales sutiles
 * - Gradiente de fondo con la paleta de marca
 */
export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  message,
  variant = "fullscreen",
  hideBrand = false,
}) => {
  const containerClass =
    variant === "fullscreen"
      ? "fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-indigo-950 via-slate-950 to-purple-950"
      : variant === "overlay"
        ? "absolute inset-0 z-40 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm"
        : "flex flex-col items-center justify-center py-16";

  const isDark = variant !== "overlay";

  return (
    <div className={containerClass} role="status" aria-live="polite">
      {/* Halo difuso de fondo */}
      {variant === "fullscreen" && (
        <>
          <div
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-indigo-600/20 blur-3xl animate-pulse"
            aria-hidden="true"
          />
          <div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-fuchsia-600/20 blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
            aria-hidden="true"
          />
        </>
      )}

      {/* Logo animado con anillos orbitales */}
      <div className="relative flex items-center justify-center">
        {/* Anillo exterior */}
        <div
          className={`absolute w-32 h-32 rounded-full border-2 ${
            isDark ? "border-indigo-400/20" : "border-indigo-400/40"
          } animate-[spin_3s_linear_infinite]`}
          style={{
            borderTopColor: "transparent",
            borderRightColor: "transparent",
          }}
          aria-hidden="true"
        />
        {/* Anillo medio (rotación inversa) */}
        <div
          className={`absolute w-24 h-24 rounded-full border-2 ${
            isDark ? "border-fuchsia-400/30" : "border-fuchsia-400/50"
          } animate-[spin_2s_linear_infinite_reverse]`}
          style={{
            borderBottomColor: "transparent",
            borderLeftColor: "transparent",
          }}
          aria-hidden="true"
        />

        {/* Logo con pulso */}
        <div className="relative animate-[pulse_2s_ease-in-out_infinite]">
          <img
            src="/branding/insignia_icon.svg"
            alt="Insignia"
            className="w-16 h-16 drop-shadow-[0_0_20px_rgba(168,85,247,0.5)]"
            draggable={false}
          />
        </div>
      </div>

      {/* Texto de marca */}
      {!hideBrand && (
        <div className="mt-8 text-center relative z-10">
          <h1
            className={`text-2xl font-bold tracking-tight ${
              isDark
                ? "bg-gradient-to-r from-indigo-200 via-fuchsia-200 to-purple-200 bg-clip-text text-transparent"
                : "text-gray-900"
            }`}
          >
            Insignia
          </h1>
          {message && (
            <p
              className={`mt-2 text-sm ${
                isDark ? "text-indigo-200/70" : "text-gray-600"
              }`}
            >
              {message}
            </p>
          )}
          {!message && (
            <div className="mt-3 flex items-center justify-center gap-1.5">
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  isDark ? "bg-indigo-300" : "bg-indigo-500"
                } animate-bounce`}
                style={{ animationDelay: "0ms" }}
              />
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  isDark ? "bg-fuchsia-300" : "bg-fuchsia-500"
                } animate-bounce`}
                style={{ animationDelay: "150ms" }}
              />
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  isDark ? "bg-purple-300" : "bg-purple-500"
                } animate-bounce`}
                style={{ animationDelay: "300ms" }}
              />
            </div>
          )}
        </div>
      )}

      <span className="sr-only">Cargando...</span>

      {/* Powered by DL Soft */}
      {variant === "fullscreen" && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <PoweredByDLSoft theme="dark" variant="simple" size="sm" />
        </div>
      )}
    </div>
  );
};

/**
 * Loader compacto inline para secciones específicas (no fullscreen).
 * Usa el estilo de marca en pequeño.
 */
export const InlineLoader: React.FC<{
  size?: "sm" | "md" | "lg";
  label?: string;
  className?: string;
}> = ({ size = "md", label, className = "" }) => {
  const dims =
    size === "sm" ? "w-5 h-5" : size === "lg" ? "w-10 h-10" : "w-7 h-7";
  const textSize =
    size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm";

  return (
    <div
      className={`inline-flex items-center gap-3 ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className="relative flex items-center justify-center">
        <div
          className={`${dims} rounded-full border-2 border-indigo-200 border-t-indigo-600 border-r-fuchsia-500 animate-spin`}
          aria-hidden="true"
        />
      </div>
      {label && (
        <span className={`${textSize} text-gray-600 font-medium`}>{label}</span>
      )}
      {!label && <span className="sr-only">Cargando...</span>}
    </div>
  );
};

export default LoadingScreen;
